import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useCatalogo } from '@/lib/catalogo';
import {
  salvarNotificacoes,
  type CanalNotificacao,
  type ParametroNotificacao,
} from '@/lib/notificacoes';

// Ícone e cor são decisão de interface: o banco guarda a chave, e trocar o
// design system muda este mapa, não a configuração. Chave sem entrada cai no
// neutro em vez de sumir da tela.
const APARENCIA: Record<string, { icone: string; bg: string; texto: string; accent: string }> = {
  treinamento: {
    icone: 'ri-award-line',
    bg: 'bg-primary-50',
    texto: 'text-primary-600',
    accent: 'accent-primary-500',
  },
  alvara: {
    icone: 'ri-file-shield-2-line',
    bg: 'bg-accent-50',
    texto: 'text-accent-600',
    accent: 'accent-accent-500',
  },
  manutencao: {
    icone: 'ri-tools-line',
    bg: 'bg-green-50',
    texto: 'text-green-600',
    accent: 'accent-green-500',
  },
  aviso_previo: {
    icone: 'ri-alarm-warning-line',
    bg: 'bg-amber-50',
    texto: 'text-amber-600',
    accent: 'accent-amber-500',
  },
};

const APARENCIA_PADRAO = {
  icone: 'ri-notification-3-line',
  bg: 'bg-secondary-50',
  texto: 'text-secondary-600',
  accent: 'accent-secondary-500',
};

const ICONE_DO_CANAL: Record<string, string> = {
  email: 'ri-mail-line',
  push: 'ri-smartphone-line',
  dashboard: 'ri-dashboard-line',
};

/** Selo que diz se o parâmetro já tem efeito — a coluna `aplicado` do banco. */
function SeloAplicado({ aplicado }: { aplicado: boolean }) {
  return aplicado ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
      <i className="ri-check-line text-[10px]"></i>
      Aplicado nos alertas
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary-100 text-secondary-900 border border-secondary-200">
      <i className="ri-time-line text-[10px]"></i>
      Sem gatilho ainda
    </span>
  );
}

function CartaoParametro({
  parametro,
  valor,
  onChange,
}: {
  parametro: ParametroNotificacao;
  valor: number;
  onChange: (dias: number) => void;
}) {
  const aparencia = APARENCIA[parametro.chave] || APARENCIA_PADRAO;
  const campoId = `parametro-${parametro.chave}`;

  const aplicar = (bruto: number) => {
    if (Number.isNaN(bruto)) return;
    onChange(Math.min(Math.max(bruto, parametro.dias_minimo), parametro.dias_maximo));
  };

  return (
    <div className="bg-background-100/50 rounded-lg p-5 border border-background-200/40">
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-lg ${aparencia.bg} flex items-center justify-center shrink-0`}
        >
          <i className={`${aparencia.icone} ${aparencia.texto}`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-foreground-900">{parametro.rotulo}</h4>
            <SeloAplicado aplicado={parametro.aplicado} />
          </div>
          <p className="text-xs text-foreground-500 mt-0.5">
            {parametro.descricao} Hoje: <span className="font-semibold">{valor} dias</span>.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <label htmlFor={campoId} className="text-xs text-foreground-600 whitespace-nowrap">
            Antecedência do alerta
          </label>
          <div className="flex items-center gap-2">
            <input
              id={campoId}
              type="number"
              min={parametro.dias_minimo}
              max={parametro.dias_maximo}
              value={valor}
              onChange={(e) => aplicar(parseInt(e.target.value, 10))}
              className="w-16 px-2 py-1 rounded-md text-xs font-bold text-foreground-800 bg-background-50 border border-background-300/60 focus:outline-none focus:ring-2 focus:ring-primary-400 text-center"
            />
            <span className="text-xs text-foreground-500">dias</span>
          </div>
        </div>
        <input
          type="range"
          aria-label={`${parametro.rotulo}: antecedência em dias`}
          min={parametro.dias_minimo}
          max={parametro.dias_maximo}
          step={1}
          value={valor}
          onChange={(e) => aplicar(Number(e.target.value))}
          className={`w-full h-2 rounded-full appearance-none cursor-pointer bg-background-200/70 ${aparencia.accent}`}
        />
        <div className="flex justify-between text-[10px] text-foreground-400">
          <span>{parametro.dias_minimo} dias</span>
          <span>{parametro.dias_maximo} dias</span>
        </div>
      </div>
    </div>
  );
}

export default function AdminNotificacoesPage() {
  const { isSuperuser } = useAuth();

  const {
    itens: parametros,
    carregando: carregandoParametros,
    erro: erroParametros,
    recarregar: recarregarParametros,
  } = useCatalogo<ParametroNotificacao>('notificacao_parametros');
  const {
    itens: canais,
    carregando: carregandoCanais,
    recarregar: recarregarCanais,
  } = useCatalogo<CanalNotificacao>('notificacao_canais');

  // Rascunho da tela. Começa vazio e é semeado quando a configuração chega —
  // inicializar no primeiro render deixaria os campos presos no valor de antes
  // de o catálogo carregar.
  const [dias, setDias] = useState<Record<string, number>>({});
  const [ativos, setAtivos] = useState<Record<string, boolean>>({});
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (parametros.length === 0) return;
    setDias(Object.fromEntries(parametros.map((p) => [p.chave, p.dias])));
  }, [parametros]);

  useEffect(() => {
    if (canais.length === 0) return;
    setAtivos(Object.fromEntries(canais.map((c) => [c.chave, c.ativo])));
  }, [canais]);

  const alterado = useMemo(() => {
    const prazoMudou = parametros.some(
      (p) => dias[p.chave] !== undefined && dias[p.chave] !== p.dias,
    );
    const canalMudou = canais.some(
      (c) => ativos[c.chave] !== undefined && ativos[c.chave] !== c.ativo,
    );
    return prazoMudou || canalMudou;
  }, [parametros, canais, dias, ativos]);

  const carregando = carregandoParametros || carregandoCanais;

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma. Faça login com as
            credenciais de administrador para acessar.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const handleSalvar = async () => {
    setSalvando(true);
    setErroSalvar(null);
    setSalvo(false);
    try {
      await salvarNotificacoes(
        parametros
          .filter((p) => dias[p.chave] !== undefined && dias[p.chave] !== p.dias)
          .map((p) => ({ chave: p.chave, dias: dias[p.chave] })),
        canais
          .filter((c) => ativos[c.chave] !== undefined && ativos[c.chave] !== c.ativo)
          .map((c) => ({ chave: c.chave, ativo: ativos[c.chave] })),
      );
      recarregarParametros();
      recarregarCanais();
      setSalvo(true);
      setTimeout(() => setSalvo(false), 3000);
    } catch (err: any) {
      setErroSalvar(err?.message || 'Erro ao salvar a configuração.');
    } finally {
      setSalvando(false);
    }
  };

  const semGatilho = parametros.filter((p) => !p.aplicado);
  const canaisSemEntrega = canais.filter((c) => !c.aplicado);

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Notificações</h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-accent-500 text-background-50">
              Super Admin
            </span>
          </div>
          <p className="text-sm text-foreground-500">
            Prazos e canais de alerta da plataforma. Valem para todos os condomínios.
          </p>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              <span className="text-sm">Carregando a configuração...</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {erroParametros && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-600"></i>
                <span className="text-sm text-red-700">
                  Erro ao carregar a configuração: {erroParametros}
                </span>
              </div>
            )}

            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              <div className="px-5 py-4 border-b border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900">
                  Parâmetros de Notificação
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  A antecedência define quando um vencimento passa a contar como &quot;a
                  vencer&quot; nos alertas e no painel.
                </p>
              </div>

              <div className="p-5 md:p-6 flex flex-col gap-6">
                {parametros.map((p) => (
                  <CartaoParametro
                    key={p.chave}
                    parametro={p}
                    valor={dias[p.chave] ?? p.dias}
                    onChange={(v) => {
                      setDias((prev) => ({ ...prev, [p.chave]: v }));
                      setSalvo(false);
                    }}
                  />
                ))}

                {/* Canais */}
                <div className="bg-background-100/50 rounded-lg p-5 border border-background-200/40">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary-50 flex items-center justify-center shrink-0">
                      <i className="ri-notification-3-line text-secondary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">
                        Canais de Notificação
                      </h4>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        Por quais canais os alertas devem sair.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    {canais.map((c) => {
                      const ativo = ativos[c.chave] ?? c.ativo;
                      return (
                        <div key={c.chave} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <i
                              className={`${ICONE_DO_CANAL[c.chave] || 'ri-notification-3-line'} text-foreground-600 text-sm`}
                            ></i>
                            <span className="text-sm text-foreground-700">{c.rotulo}</span>
                            <SeloAplicado aplicado={c.aplicado} />
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={ativo}
                            aria-label={c.rotulo}
                            onClick={() => {
                              setAtivos((prev) => ({ ...prev, [c.chave]: !ativo }));
                              setSalvo(false);
                            }}
                            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
                              ativo ? 'bg-primary-500' : 'bg-background-300/60'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-background-50 shadow-sm transition-transform ${
                                ativo ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/*
                  A tela dizia que "as notificações são disparadas automaticamente"
                  para tudo o que ela mostra. Metade disso ainda não existe, e a
                  distinção vem da coluna `aplicado` — não de um texto no React que
                  envelhece sem ninguém notar.
                */}
                <div className="flex items-start gap-3 p-4 rounded-lg bg-primary-50/50 border border-primary-200/50">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-information-line text-primary-600 text-sm"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primary-800">O que já tem efeito</p>
                    <p className="text-xs text-primary-600 mt-0.5 leading-relaxed">
                      Os prazos marcados como aplicados entram na apuração do painel e da situação
                      dos treinamentos, no banco: alterar aqui muda o que aparece como &quot;a
                      vencer&quot; na próxima consulta, em todos os condomínios.
                      {semGatilho.length > 0 && (
                        <>
                          {' '}
                          {semGatilho.map((p) => p.rotulo).join(' e ')} ainda{' '}
                          {semGatilho.length > 1 ? 'ficam' : 'fica'} apenas guardado: não há gatilho
                          consumindo esse prazo.
                        </>
                      )}
                      {canaisSemEntrega.length > 0 && (
                        <> Os canais ficam registrados, mas o despacho ainda não foi construído.</>
                      )}
                    </p>
                  </div>
                </div>

                {/* Salvar */}
                <div className="flex items-center justify-end gap-3">
                  {erroSalvar && (
                    <span className="text-sm text-red-700 flex items-center gap-1.5">
                      <i className="ri-error-warning-line"></i>
                      {erroSalvar}
                    </span>
                  )}
                  {salvo && (
                    <span className="text-sm font-medium text-green-700 bg-green-50 px-3 py-1.5 rounded-md flex items-center gap-1.5">
                      <i className="ri-check-line text-sm"></i>
                      Configuração salva
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleSalvar}
                    disabled={salvando || !alterado}
                    className="whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {salvando ? (
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-loader-4-line animate-spin text-sm"></i>
                        Salvando...
                      </span>
                    ) : (
                      'Salvar Configurações'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
